import { array, boolean, discriminatedUnion, enum as enumFactory, literal, number, strictObject, string } from "../../lazy-zod.js";
import { openMeldWebUrlSchema } from "../../navigation-action.js";
import { workingLanguageGuidanceSchema } from "./working-language.js";
import { centerAgentInteractionSourceSchema } from "../protocol/center-agent-interaction-source.js";
import { spaceCallerContextSchema } from "../protocol/space-caller-context.js";
import { agentDelegationSnapshotSchema, agentTaskCompletionObservationSchema, agentTaskHandleSchema, agentTaskTerminalOutcomeSchema, areAgentTaskTerminalOutcomesEqual, buildAgentTaskKey } from "./agent-task-coordination.js";
import { buildMessageChannelConversationId, messageChannelConversationLocatorSchema, messageChannelParticipantIdentitySchema, messageChannelProviderSchema, messageChannelResourceDescriptorSchema } from "./message-channel.js";
import { centerAgentRoutineAuthorizedActionsSchema, centerAgentRoutineExternalActionPolicySchema } from "../http/center-agent-routines.js";
import { MAX_SPACE_GUIDE_LENGTH } from "../http/space-meta.js";
import { channelContextSchema } from "./channel-context.js";
//#region src/schemas/runtime/remote-agent-turn-envelope.ts
const nonEmptyStringSchema = string().trim().min(1);
const REMOTE_AGENT_TRIGGER_TEXT_MAX_CHARS = 4e3;
const REMOTE_AGENT_TURN_ENVELOPE_MEMORY_ITEM_TEXT_MAX_CHARS = 1200;
const REMOTE_AGENT_WORK_LOOP_CONTINUATION_MAX_ATTEMPTS = 8;
const remoteAgentWorkLoopCheckpointSchema = strictObject({
	reason: literal("tool_call_budget_exhausted"),
	toolCallLimit: number().int().positive(),
	v: literal(1)
});
const remoteAgentWorkLoopContinuationSchema = strictObject({
	attempt: number().int().positive().max(8),
	previousSubmissionId: nonEmptyStringSchema,
	reason: literal("tool_call_budget_exhausted"),
	v: literal(1)
});
const remoteAgentInteractionEnvironmentSchema = enumFactory([
	"requester_private",
	"organization_shared",
	"externally_shared"
]);
const centerAgentContextHandlingSchema = enumFactory([
	"use_and_share",
	"use_privately",
	"share_when_authorized",
	"never_available"
]);
const centerAgentRequesterIdentitySchema = enumFactory([
	"verified_member",
	"connected_organization_member",
	"external_participant",
	"organization_routine",
	"agent_profile"
]);
const centerAgentResultAudienceSchema = enumFactory([
	"requester_only",
	"organization_members",
	"external_conversation"
]);
const centerAgentContextScopePlanSchema = strictObject({
	authenticationSecrets: literal("never_available"),
	interactionEnvironment: remoteAgentInteractionEnvironmentSchema,
	memberMemory: centerAgentContextHandlingSchema,
	organizationDocuments: centerAgentContextHandlingSchema,
	organizationMemory: centerAgentContextHandlingSchema,
	organizationSkills: centerAgentContextHandlingSchema,
	protectedInformation: centerAgentContextHandlingSchema,
	requesterIdentity: centerAgentRequesterIdentitySchema,
	resultAudience: centerAgentResultAudienceSchema,
	v: literal(1)
});
const remoteAgentTurnEnvelopePersonSenderShape = {
	callerContext: spaceCallerContextSchema.optional(),
	id: nonEmptyStringSchema,
	displayName: nonEmptyStringSchema,
	ownerUserId: nonEmptyStringSchema.optional(),
	profileId: nonEmptyStringSchema.optional(),
	role: nonEmptyStringSchema.optional(),
	messageChannelIdentity: messageChannelParticipantIdentitySchema.optional(),
	verified: boolean()
};
const remoteAgentTurnEnvelopeSenderSchema = discriminatedUnion("kind", [
	strictObject({
		kind: literal("member"),
		...remoteAgentTurnEnvelopePersonSenderShape
	}),
	strictObject({
		kind: literal("agent_profile"),
		...remoteAgentTurnEnvelopePersonSenderShape
	}),
	strictObject({
		kind: literal("routine"),
		routineId: nonEmptyStringSchema,
		createdByUserId: nonEmptyStringSchema,
		delegatedBy: strictObject({
			id: nonEmptyStringSchema,
			displayName: nonEmptyStringSchema,
			ownerUserId: nonEmptyStringSchema,
			profileId: nonEmptyStringSchema.optional(),
			role: nonEmptyStringSchema.optional(),
			verified: literal(true)
		})
	})
]);
const remoteAgentTurnEnvelopeSpaceSchema = strictObject({
	id: nonEmptyStringSchema,
	name: nonEmptyStringSchema,
	kind: enumFactory(["dm", "space"]),
	isCenterAgentDm: boolean(),
	guide: nonEmptyStringSchema.max(MAX_SPACE_GUIDE_LENGTH).optional()
});
const remoteAgentTurnEnvelopeOrganizationSchema = strictObject({
	id: nonEmptyStringSchema,
	name: nonEmptyStringSchema,
	personal: boolean(),
	memberCount: number().int().nonnegative(),
	webUrl: openMeldWebUrlSchema.optional(),
	workingLanguage: workingLanguageGuidanceSchema.optional()
});
const remoteAgentTurnEnvelopeCenterAgentSchema = strictObject({
	profileName: nonEmptyStringSchema,
	profileHandle: nonEmptyStringSchema
});
const remoteAgentSlackSearchAuthorizationSchema = strictObject({ encryptedAuthorization: nonEmptyStringSchema.max(8192) });
const remoteAgentRoutineDestinationSchema = discriminatedUnion("kind", [
	strictObject({
		kind: literal("message_channel"),
		locator: messageChannelConversationLocatorSchema
	}),
	strictObject({
		kind: literal("space"),
		spaceId: nonEmptyStringSchema
	}),
	strictObject({
		channelId: nonEmptyStringSchema,
		conversationId: nonEmptyStringSchema,
		kind: literal("slack_thread"),
		label: nonEmptyStringSchema.optional(),
		teamId: nonEmptyStringSchema,
		threadTs: nonEmptyStringSchema
	})
]);
const remoteAgentRoutineContinuationSchema = strictObject({
	delegation: agentDelegationSnapshotSchema,
	delegationId: nonEmptyStringSchema,
	outcome: agentTaskTerminalOutcomeSchema,
	subscriptionId: nonEmptyStringSchema,
	watchedTask: agentTaskHandleSchema
});
const remoteAgentTurnEnvelopeTriggerSchema = discriminatedUnion("source", [
	strictObject({ source: literal("web_dm") }),
	strictObject({ source: literal("space_wake") }),
	strictObject({
		source: literal("slack"),
		teamId: nonEmptyStringSchema.optional(),
		channelId: nonEmptyStringSchema.optional(),
		threadTs: nonEmptyStringSchema.optional(),
		conversationId: nonEmptyStringSchema.optional(),
		channelName: nonEmptyStringSchema.optional(),
		replyPlacement: enumFactory(["conversation", "thread"]).optional(),
		searchAuthorization: remoteAgentSlackSearchAuthorizationSchema.optional()
	}),
	strictObject({
		source: literal("message_channel"),
		routineLocator: messageChannelConversationLocatorSchema.optional(),
		provider: messageChannelProviderSchema,
		connectionId: nonEmptyStringSchema,
		conversationId: nonEmptyStringSchema,
		filePublicationSupported: literal(true).optional(),
		resources: array(messageChannelResourceDescriptorSchema).max(10).optional()
	}),
	strictObject({
		authorizedActions: centerAgentRoutineAuthorizedActionsSchema.default([]),
		externalActionPolicy: centerAgentRoutineExternalActionPolicySchema.optional(),
		source: literal("routine"),
		routineId: nonEmptyStringSchema,
		runId: nonEmptyStringSchema,
		scheduledFor: number().int().nonnegative(),
		routineName: nonEmptyStringSchema.max(120),
		destination: remoteAgentRoutineDestinationSchema.optional(),
		continuation: remoteAgentRoutineContinuationSchema.optional()
	}),
	strictObject({
		source: literal("member_research"),
		taskId: nonEmptyStringSchema,
		subjectUserId: nonEmptyStringSchema,
		subjectDisplayName: nonEmptyStringSchema
	}),
	strictObject({
		delegation: agentDelegationSnapshotSchema.optional(),
		delegationId: nonEmptyStringSchema,
		outcome: agentTaskTerminalOutcomeSchema,
		source: literal("task_continuation"),
		subscriptionId: nonEmptyStringSchema,
		watchedTask: agentTaskHandleSchema
	}),
	strictObject({
		outcome: agentTaskCompletionObservationSchema,
		source: literal("task_watch_continuation"),
		subscriptionId: nonEmptyStringSchema,
		watchedTask: agentTaskHandleSchema,
		watchId: nonEmptyStringSchema
	})
]);
const remoteAgentTurnEnvelopeMemorySourceSchema = enumFactory([
	"ca_dm",
	"explicit_tool",
	"space_conversation",
	"slack_conversation",
	"message_channel_conversation"
]);
const remoteAgentTurnEnvelopeMemorySubjectSchema = enumFactory(["member", "organization"]);
const remoteAgentTurnEnvelopeMemoryItemTextSchema = nonEmptyStringSchema.max(REMOTE_AGENT_TURN_ENVELOPE_MEMORY_ITEM_TEXT_MAX_CHARS);
const remoteAgentTurnEnvelopeMemoryItemSchema = discriminatedUnion("source", [strictObject({
	source: enumFactory([
		"ca_dm",
		"explicit_tool",
		"space_conversation",
		"slack_conversation"
	]),
	subject: remoteAgentTurnEnvelopeMemorySubjectSchema,
	text: remoteAgentTurnEnvelopeMemoryItemTextSchema
}), strictObject({
	source: literal("message_channel_conversation"),
	provider: messageChannelProviderSchema,
	subject: remoteAgentTurnEnvelopeMemorySubjectSchema,
	text: remoteAgentTurnEnvelopeMemoryItemTextSchema
})]);
const remoteAgentTurnEnvelopeMemorySchema = strictObject({
	items: array(remoteAgentTurnEnvelopeMemoryItemSchema).max(8),
	truncated: boolean()
});
const remoteAgentTurnEnvelopeSchema = strictObject({
	sender: remoteAgentTurnEnvelopeSenderSchema,
	space: remoteAgentTurnEnvelopeSpaceSchema,
	organization: remoteAgentTurnEnvelopeOrganizationSchema,
	centerAgent: remoteAgentTurnEnvelopeCenterAgentSchema,
	trigger: remoteAgentTurnEnvelopeTriggerSchema,
	contextScopePlan: centerAgentContextScopePlanSchema.optional(),
	interactionEnvironment: remoteAgentInteractionEnvironmentSchema.optional(),
	centerAgentInteractionSource: centerAgentInteractionSourceSchema.optional(),
	channelContext: channelContextSchema.optional(),
	memory: remoteAgentTurnEnvelopeMemorySchema.optional(),
	workLoopContinuation: remoteAgentWorkLoopContinuationSchema.optional()
}).superRefine((envelope, context) => {
	const hasRoutineSender = envelope.sender.kind === "routine";
	if (hasRoutineSender !== (envelope.trigger.source === "routine")) {
		context.addIssue({
			code: "custom",
			message: "Routine sender and trigger variants must be used together.",
			path: [hasRoutineSender ? "trigger" : "sender"]
		});
		return;
	}
	if (envelope.sender.kind === "routine" && envelope.trigger.source === "routine" && envelope.sender.routineId !== envelope.trigger.routineId) context.addIssue({
		code: "custom",
		message: "Routine sender and trigger identifiers must match.",
		path: ["trigger", "routineId"]
	});
	if (envelope.sender.kind === "routine" && envelope.sender.delegatedBy.ownerUserId !== envelope.sender.createdByUserId) context.addIssue({
		code: "custom",
		message: "Routine delegation must belong to the user who created the Routine.",
		path: [
			"sender",
			"delegatedBy",
			"ownerUserId"
		]
	});
	if (envelope.trigger.source === "routine" && envelope.trigger.destination?.kind === "message_channel" && (envelope.trigger.destination.locator.provider === "slack" || buildMessageChannelConversationId(envelope.trigger.destination.locator) !== envelope.space.id)) context.addIssue({
		code: "custom",
		message: "Routine destination must match its conversation.",
		path: ["trigger", "destination"]
	});
	if (envelope.trigger.source === "routine" && envelope.trigger.destination && envelope.trigger.destination.kind === "space" && envelope.trigger.destination.spaceId !== envelope.space.id) context.addIssue({
		code: "custom",
		message: "Routine Space destination must match the envelope conversation.",
		path: [
			"trigger",
			"destination",
			"spaceId"
		]
	});
	if (envelope.trigger.source === "routine" && envelope.trigger.destination && envelope.trigger.destination.kind === "slack_thread" && envelope.trigger.destination.conversationId !== envelope.space.id) context.addIssue({
		code: "custom",
		message: "Routine Slack destination must match the envelope conversation.",
		path: [
			"trigger",
			"destination",
			"conversationId"
		]
	});
	if (envelope.trigger.source === "member_research" && (envelope.sender.kind !== "member" || !envelope.sender.verified || !envelope.sender.ownerUserId || !envelope.sender.profileId)) context.addIssue({
		code: "custom",
		message: "Member research turns require a verified human member sender.",
		path: ["sender"]
	});
	if (envelope.trigger.source === "task_continuation" && (envelope.sender.kind !== "member" || !envelope.sender.verified || !envelope.sender.ownerUserId || !envelope.sender.profileId)) context.addIssue({
		code: "custom",
		message: "Task continuation turns require the original verified human member sender.",
		path: ["sender"]
	});
	validateTaskContinuationTrigger(envelope.trigger, context);
	validateMessageChannelTurn(envelope, context);
});
function validateMessageChannelTurn(envelope, context) {
	if (envelope.trigger.source !== "message_channel") return;
	const locator = envelope.trigger.routineLocator;
	if (locator && (locator.provider !== envelope.trigger.provider || locator.connectionId !== envelope.trigger.connectionId || buildMessageChannelConversationId(locator) !== envelope.trigger.conversationId)) context.addIssue({
		code: "custom",
		message: "Routine origin must match the Message Channel trigger.",
		path: ["trigger", "routineLocator"]
	});
	if (envelope.trigger.conversationId !== envelope.space.id) context.addIssue({
		code: "custom",
		message: "Message Channel trigger must match the envelope conversation.",
		path: ["trigger", "conversationId"]
	});
	if (envelope.channelContext && (envelope.channelContext.provider !== envelope.trigger.provider || envelope.channelContext.conversationId !== envelope.trigger.conversationId)) context.addIssue({
		code: "custom",
		message: "Message Channel context must match the trigger provider and conversation.",
		path: ["channelContext"]
	});
}
function validateTaskContinuationTrigger(trigger, context) {
	let continuation = null;
	if (trigger.source === "task_continuation" && trigger.delegation) continuation = {
		delegation: trigger.delegation,
		delegationId: trigger.delegationId,
		outcome: trigger.outcome,
		subscriptionId: trigger.subscriptionId,
		watchedTask: trigger.watchedTask
	};
	else if (trigger.source === "routine") continuation = trigger.continuation ?? null;
	if (!continuation) return;
	const { delegation, delegationId, outcome, watchedTask } = continuation;
	if (delegation.delegationId !== delegationId) context.addIssue({
		code: "custom",
		message: "Task continuation delegation identifiers must match the snapshot.",
		path: [
			"trigger",
			"delegation",
			"delegationId"
		]
	});
	const watchedTaskKey = buildAgentTaskKey(watchedTask);
	const watchedChild = delegation.children.find((child) => buildAgentTaskKey(child.task) === watchedTaskKey);
	if (!watchedChild) context.addIssue({
		code: "custom",
		message: "Task continuation watched task must identify a snapshot child.",
		path: ["trigger", "watchedTask"]
	});
	else if (!(watchedChild.terminalOutcome && areAgentTaskTerminalOutcomesEqual(watchedChild.terminalOutcome, outcome))) context.addIssue({
		code: "custom",
		message: "Task continuation outcome must match the watched snapshot child.",
		path: ["trigger", "outcome"]
	});
}
//#endregion
export { REMOTE_AGENT_TRIGGER_TEXT_MAX_CHARS, REMOTE_AGENT_TURN_ENVELOPE_MEMORY_ITEM_TEXT_MAX_CHARS, REMOTE_AGENT_WORK_LOOP_CONTINUATION_MAX_ATTEMPTS, centerAgentContextHandlingSchema, centerAgentContextScopePlanSchema, centerAgentRequesterIdentitySchema, centerAgentResultAudienceSchema, remoteAgentInteractionEnvironmentSchema, remoteAgentSlackSearchAuthorizationSchema, remoteAgentTurnEnvelopeCenterAgentSchema, remoteAgentTurnEnvelopeMemoryItemSchema, remoteAgentTurnEnvelopeMemorySchema, remoteAgentTurnEnvelopeMemorySourceSchema, remoteAgentTurnEnvelopeMemorySubjectSchema, remoteAgentTurnEnvelopeOrganizationSchema, remoteAgentTurnEnvelopeSchema, remoteAgentTurnEnvelopeSenderSchema, remoteAgentTurnEnvelopeSpaceSchema, remoteAgentTurnEnvelopeTriggerSchema, remoteAgentWorkLoopCheckpointSchema, remoteAgentWorkLoopContinuationSchema };

//# sourceMappingURL=remote-agent-turn-envelope.js.map